import { Component, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import moment from 'moment';
import { ApiClientConstant, Table } from '@cureskin/api-client';
import { AppConfig } from '../../app/app.config';
import { ConnectionService } from '../../services/connection-service';
import { AddConcernModal } from './add-concern-popup';
import { Broadcaster } from '../broadcaster';
import { InputType } from '../../../typings/client/input';
import { DataUploadModal } from '../modal/dataUploadModal';
import { InstantCheckupImageView } from '../instant-checkup-image-view';
import { ImageComparisonPopup } from '../image-comparison-popup';
import { ComparisonViewerComponent } from '../comparison-viewer/comparison-viewer.component';
import { FollowUpReportCreationService } from '../../services/follow-up-report/report-creation.service';
import {Entity, EntityEnum} from "@cureskin/api-client-rn";

interface ReportObject {
  imageConfigs: Array<{ beforeInstantId?: string, afterInstantId?: string, imageConfig?: any }>;
  treatmentProgress: Array<{
    issue: string,
    concernLanguageString: any,
    score: number,
  }>;
  investigations: {
    tests?: { test: string, required: boolean }[],
    name?: string,
    testsStatus?: { name: string, status: boolean }[],
    feedback?: string,
  };
  treatmentOutcome: { selectedTreatmentOutcome: string };
}

@Component({
    selector: 'followup-report-creation',
    templateUrl: './followUp-report-creation.html',
    styleUrls: ['./followUp-report-creation.scss'],
    standalone: false
})
export class FollowUpReportCreationComponent {
  @Input('class')
  set onUpdateClass(className: string) {
    if (!className) return;
    this.class = className;
    this.fetchDataAndStartComparing();
  }
  @Input('user') user: any;
  @Input('followUp') followUp: any;
  @Input('regimen')
  set onUpdateRegimen(regimen: any) {
    if (!regimen) return;
    this.regimen = regimen;
    this.fetchFollowUpSopOptions();
  }
  @Output() createReport: EventEmitter<Partial<ReportObject>> = new EventEmitter();
  @Output() changeRegimen: EventEmitter<{ changes: Array<any>; onHoldDuration?: number}> = new EventEmitter();
  @Output() changeVoiceNote: EventEmitter<string> = new EventEmitter();
  @Output() disableRegimenEdit: EventEmitter<boolean> = new EventEmitter();
  @ViewChild('secondaryComparison') SecondaryComparisonViewerComponent: ComparisonViewerComponent;
  @ViewChild('primaryComparison') PrimaryComparisonViewerComponent: ComparisonViewerComponent;
  reportObject: Partial<ReportObject> = { imageConfigs: [{}, {}] };
  class: string;
  mainConcernList: Array<any>;
  investigationOptions: Array<{ display: string, value: Table.Investigations}> = [];
  primaryInstantCheckUpIds: Array<string>;
  secondaryInstantCheckUpIds: Array<string>;
  treatmentOutcomeOptions: Array<InputType.SelectTextOption> = [];
  instantCheckups: Array<any> = [];
  primaryComparisonTitle: string;
  secondaryComparisonTitle: string;
  treatmentProgress: Array<{ issue: string, concernLanguageString: any, score: number }> = [];
  followUpSopOptions: Array<InputType.SelectTextOption> = [];
  followUpSop: {
    selectedFollowUpSopName?: string,
    changes?: Array<any>,
    currentFollowUpSop?: Array<any>,
    selectedFollowUpSop?: {
      changes: Array<any>,
      tests: any,
      nextFollowUps: Array<any>,
      treatmentProgress: Array<any>,
      audioTemplateName: string,
      name: string,
      onHoldDurationInDays: number,
    },
  } = {};
  regimen: any;
  followUpSopEnabled: boolean;
  showSteroidQuestion: boolean = true;
  selectedPregnancyStatus: string = 'NONE';
  pregnancyTags: Array<string> = [ApiClientConstant.User.MarketingTags.NEW_MOM,
    ApiClientConstant.User.MarketingTags.PREGNANT,
    ApiClientConstant.User.MarketingTags.PLANNING_FOR_PREGNANCY];

  constructor(public appConfig: AppConfig,
    private dialog: MatDialog,
    private broadcaster: Broadcaster,
    private connectionService: ConnectionService,
    private readonly reportCreationService: FollowUpReportCreationService) {}

  async ngOnInit(): Promise<void> {
    await this.fetchInvestigations();
    this.fetchTreatmentOutcomeOptions();
    await this.fetchFollowUpSopOptions();
    this.initPregnancyStatus();
  }

  initPregnancyStatus(): void {
    const marketingTags = this.user?.get('marketingTags') || [];
    if (marketingTags.includes(ApiClientConstant.User.MarketingTags.PREGNANT)) {
      this.selectedPregnancyStatus = 'PREGNANT';
    } else if (marketingTags.includes(ApiClientConstant.User.MarketingTags.PLANNING_FOR_PREGNANCY)) {
      this.selectedPregnancyStatus = 'PLANNING_FOR_PREGNANCY';
    } else if (marketingTags.includes(ApiClientConstant.User.MarketingTags.NEW_MOM)) {
      this.selectedPregnancyStatus = 'NEW_MOM';
    } else {
      this.selectedPregnancyStatus = 'NONE';
    }
  }

  async reset(): Promise<void> {
    this.primaryInstantCheckUpIds = [];
    this.secondaryInstantCheckUpIds = [];
    this.treatmentProgress = [];
    this.reportObject = { imageConfigs: [{}, {}] };
    this.mainConcernList = [];
  }

  async fetchDataAndStartComparing(): Promise<void> {
    await this.reset();
    this.updateTitleOnBasisOfClass();
    await Promise.all([
      this.fetchPhotosAndAlign(this.class as keyof typeof EntityEnum.InstantCheckup.Type),
      this.fetchMainConcernList(),
      this.findDetectedConcerns(),
    ]);
  }

  updateTitleOnBasisOfClass(): void {
    switch (this.class) {
      case ApiClientConstant.FollowUpReport.Type.FACE: {
        this.primaryComparisonTitle = 'Front face comparison';
        this.secondaryComparisonTitle = 'Side face comparison';
        break;
      }
      case ApiClientConstant.FollowUpReport.Type.HAIR: {
        this.primaryComparisonTitle = 'Hair front comparison';
        this.secondaryComparisonTitle = 'Hair top comparison';
        break;
      }
      case ApiClientConstant.FollowUpReport.Type.BODY: {
        this.primaryComparisonTitle = 'Body comparison';
        break;
      }
      default:
    }
  }

  async fetchMainConcernList(): Promise<void> {
    this.mainConcernList = await this.connectionService.fetchMainConcerns({
      where: { class: this.class, section: 'TREATMENT' },
      include: ['displayNameLanguageString'],
      project: ['displayNameLanguageString.en' as 'displayNameLanguageString', 'aiClassName'],
    });
  }

  fetchTreatmentOutcomeOptions(): void {
    this.treatmentOutcomeOptions = [
      { display: 'Change as expected', value: 'CHANGE_AS_EXPECTED' },
      { display: 'Shift to secondary concern', value: 'SHIFT_TO_SECONDARY_CONCERN' },
      { display: 'Shift to maintenance', value: 'SHIFT_TO_MAINTENANCE' },
      { display: 'Slow improvement', value: 'SLOW_IMPROVEMENT' },
      { display: 'No change', value: 'NO_CHANGE' },
      { display: 'Slightly increased', value: 'SLIGHTLY_INCREASED' },
      { display: 'Flare up', value: 'FLARE_UP' },
    ];
  }

  // treatment progress section
  deleteBar(index: number): void {
    this.treatmentProgress.splice(index, 1);
    this.setCreateReport();
  }

  updateScore(index: number, score: number): void {
    this.treatmentProgress[index].score = score;
    this.setCreateReport();
  }

  addNewConcern(): void {
    const dialogRef = this.dialog.open(AddConcernModal, {
      data: { mainConcernList: this.mainConcernList },
    });
    dialogRef.afterClosed().subscribe(async (result: any) => {
      if (!result) return;
      const existingConcern = this.treatmentProgress.map((progress: any) => progress.issue);
      result.concernLanguageStrings.forEach((concern: any) => {
        if (!existingConcern.includes(concern.get('en'))) {
          this.treatmentProgress.push({
            issue: concern.get('en'),
            concernLanguageString: concern,
            score: 1,
          });
        }
      });
      this.setCreateReport();
    });
  }

  async findDetectedConcerns(): Promise<void> {
    const instantCheckups = this.instantCheckups.filter((each: any) => this.primaryInstantCheckUpIds.includes(each.objectId));
    const concernsDetected = [];
    instantCheckups.forEach((each: any) => {
      if (each.aiResponse?.result) {
        const aiResponseResult = each.aiResponse.result;
        concernsDetected.push(...(aiResponseResult.filter((each_: any) => each_.Condition === 'Detected')));
      }
    });
    if (this.class === ApiClientConstant.Regimen.Class.FACE) {
      const aiClassList: string[] = concernsDetected.map((each: any) => each.class);
      this.mainConcernList.forEach((each: any) => {
        if (!each.get('aiClassName')) return;
        each.get('aiClassName').forEach((className: string) => {
          if (aiClassList.includes(className)) {
            this.treatmentProgress.push({ issue: each.get('displayNameLanguageString')?.get('en'), concernLanguageString: each, score: 1 });
          }
        });
      });
    }
    this.reportObject.treatmentProgress = this.treatmentProgress;
    this.setCreateReport();
  }

  async fetchInvestigations(): Promise<void> {
    const investigations = await this.connectionService.fetchInvestigations();
    investigations.forEach((investigation: Table.Investigations) => {
      this.investigationOptions.push({ display: `${investigation.get('name')} (${investigation.get('tests')?.length})`,
        value: investigation });
    });
    const emptyTest = new Table.Investigations();
    emptyTest.set('name', 'No Tests');
    emptyTest.set('tests', []);
    this.investigationOptions.unshift({ display: 'No Tests', value: emptyTest });
  }

  addCustomTest(): void {
    const dialogRef = this.dialog.open(DataUploadModal, { data: { title: 'Custom Test Name' } });
    dialogRef.afterClosed().subscribe(async (data: string) => {
      if (!data) return;
      if (!this.reportObject.investigations) {
        this.broadcaster.broadcast('NOTIFY', {
          message: 'First Choose Investigation',
          type: this.appConfig.Shared.Toast.Type.ERROR,
        });
        return;
      }
      if (!this.reportObject.investigations.tests) {
        this.reportObject.investigations.tests = [];
      }
      this.reportObject.investigations.tests.push({ test: data, required: true });
      this.reportObject.investigations.testsStatus = this.reportObject.investigations.tests
        .map((t: { test: string, required: boolean }) => ({ name: t.test, status: false }));
      this.setCreateReport();
    });
  }

  removeTestFromInvestigation(index: any): void {
    if (index >= 0) this.reportObject.investigations.tests.splice(index, 1);
    this.reportObject.investigations.testsStatus = this.reportObject.investigations.tests
      .map((t: { test: string, required: boolean }) => ({ name: t.test, status: false }));
    this.setCreateReport();
  }

  onSelectInvestigation(investigation: any): void {
    if (!investigation) return;
    if (!investigation.get('tests').length) {
      this.reportObject.investigations = {};
      this.setCreateReport();
      return;
    }
    this.reportObject.investigations = {
      tests: investigation.get('tests'),
      name: investigation.get('name'),
      testsStatus: investigation.get('tests').map((each: { test: string, required: boolean }) => ({ name: each.test, status: false })),
      feedback: '',
    };
    this.setCreateReport();
  }

  onSelectTreatmentOutcome(outcome: string): void {
    if (!outcome) return;
    this.reportObject.treatmentOutcome = {
      selectedTreatmentOutcome: outcome,
    };
    this.setCreateReport();
  }

  selectBeforePhotoForInvestigation(index: number): void {
    // const query = {
    //   userId: this.user.get('username'),
    //   descending: 'createdAt',
    //   project: ['imagePath', 'type', 'compressedImagePath'],
    //   limit: 20,
    //   dateRange: {},
    // };
    let dateRange = { };
    if (this.followUp.get('markedReadyOn')) {
      const date = moment(this.followUp.get('markedReadyOn')).subtract(2, 'days').toDate();
      dateRange = { startDate: date };
    }
    const dialogRef = this.dialog.open(InstantCheckupImageView, { data: { userId: this.user.get('username'), dateRange } });
    dialogRef.afterClosed().subscribe(async (data: any) => {
      this.updateInstantCheckups(data, index);
    });
  }

  updateInstantCheckups(data: any, index: number): void {
    if (index === 0) {
      if (!this.primaryInstantCheckUpIds) this.primaryInstantCheckUpIds = [];
      if (data?.instantCheckUp?.objectId && this.primaryInstantCheckUpIds.length < 2) {
        this.primaryInstantCheckUpIds.push(data?.instantCheckUp?.objectId);
      }
      if (this.primaryInstantCheckUpIds.length === 2) {
        this.PrimaryComparisonViewerComponent.loadData(this.primaryInstantCheckUpIds);
      }
      return;
    }
    if (!this.secondaryInstantCheckUpIds) this.secondaryInstantCheckUpIds = [];
    if (data?.instantCheckUp?.objectId && this.secondaryInstantCheckUpIds.length < 2) {
      this.secondaryInstantCheckUpIds.push(data?.instantCheckUp?.objectId);
    }
    if (this.secondaryInstantCheckUpIds.length === 2) {
      this.SecondaryComparisonViewerComponent.loadData(this.secondaryInstantCheckUpIds);
    }
  }

  selectAfterPhotoForInvestigation(index: number): void {
    let dateRange = { };
    if (this.followUp.get('markedReadyOn')) {
      const date = moment(this.followUp.get('markedReadyOn')).subtract(2, 'days').toDate();
      dateRange = { endDate: date };
    }
    const dialogRef = this.dialog.open(InstantCheckupImageView, { data: { userId: this.user.get('username'), dateRange } });
    dialogRef.afterClosed().subscribe(async (data: any) => {
      this.updateInstantCheckups(data, index);
    });
  }

  async openImageComparisonPopup(index: number): Promise<void> {
    const instantCheckupIds = index === 0 ? this.primaryInstantCheckUpIds : this.secondaryInstantCheckUpIds;
    const params = {
      username: this.user.get('username'),
      id1: instantCheckupIds[0],
      id2: instantCheckupIds[1],
      index: 0,
    };
    const dialogRef = this.dialog.open(ImageComparisonPopup, { width: '90%', maxWidth: '90vw', data: { params } });
    dialogRef.afterClosed().subscribe(async (data: { config: any, params: any}) => {
      if (!data) return;
      let beforeInstant = this.instantCheckups.find((each: any) => each.objectId === data.params.id1);
      if (!beforeInstant) {
        beforeInstant = JSON.parse(JSON.stringify(await this.connectionService.findOneInstantCheckUp({
          where: { objectId: data.params.id1 },
          project: ['aiResponse', 'type'],
        })));
      }
      let afterInstant = this.instantCheckups.find((each: any) => each.objectId === data.params.id2);
      if (!afterInstant) {
        afterInstant = JSON.parse(JSON.stringify(await this.connectionService.findOneInstantCheckUp({
          where: { objectId: data.params.id2 },
          project: ['aiResponse', 'type'],
        })));
      }
      if (afterInstant.createdAt < beforeInstant.createdAt) {
        const temp = beforeInstant;
        beforeInstant = afterInstant;
        afterInstant = temp;
      }
      const imageConfig = {
        imageConfig: data.config,
        beforeInstantId: beforeInstant.objectId,
        afterInstantId: afterInstant.objectId,
      };
      if (index === 0) {
        this.primaryInstantCheckUpIds = [beforeInstant.objectId, afterInstant.objectId];
      } else {
        this.secondaryInstantCheckUpIds = [beforeInstant.objectId, afterInstant.objectId];
      }
      if (!data?.config?.before || !data?.config?.after) return;
      this.reportObject.imageConfigs[index] = imageConfig;
      this.setCreateReport();
    });
  }

  setImageConfig(event: any, index: number): void {
    if (!event) return;
    this.reportObject.imageConfigs[index] = event;
    this.setCreateReport();
  }

  setCreateReport(): void {
    this.createReport.emit(this.reportObject);
  }

  async fetchPhotosAndAlign(type: keyof typeof EntityEnum.InstantCheckup.Type): Promise<void> {
    try {
      ({ instantCheckups: this.instantCheckups,
        imageConfigs: this.reportObject.imageConfigs,
        primaryIds: this.primaryInstantCheckUpIds,
        secondaryIds: this.secondaryInstantCheckUpIds } = await this.reportCreationService.buildAutoImageConfigs(this.user,
        type,
        this.followUp.get('followUpCount')));
    } catch (error) {

      return;
    }
    if (this.primaryInstantCheckUpIds.length === 2) {
      this.PrimaryComparisonViewerComponent.loadData(this.primaryInstantCheckUpIds);
    }
    if (this.secondaryInstantCheckUpIds.length === 2) {
      this.SecondaryComparisonViewerComponent.loadData(this.secondaryInstantCheckUpIds);
    }
    this.setCreateReport();
  }

  deleteComparison(index: number): void {
    this.reportObject.imageConfigs[index] = {};
    switch (index) {
      case 0: {
        this.primaryInstantCheckUpIds = [];
        this.PrimaryComparisonViewerComponent?.loadData(this.primaryInstantCheckUpIds);
        break;
      } case 1: {
        this.secondaryInstantCheckUpIds = [];
        this.SecondaryComparisonViewerComponent.loadData(this.secondaryInstantCheckUpIds);
        break;
      }
      default:
    }
    this.setCreateReport();
  }

  async fetchFollowUpSopOptions(): Promise<void> {
    if (!this.regimen?.get('followUpSop')?.length || this.regimen.get('disableFollowUpSop')) return;

    if (this.followUp.get('followUpCount') === 1) {
      this.regimen.set('followUpSopTransition', []);
    }
    const followUpSopTransition = this.regimen.get('followUpSopTransition') || [];
    const followUpSop = this.regimen.get('followUpSop');

    // if till this followup we are following followupSop then only we can use followupSop
    if (followUpSopTransition?.length !== this.followUp.get('followUpCount') - 1) {
      return;
    }
    this.followUpSop.currentFollowUpSop = followUpSop;
    if (!followUpSopTransition?.length) {
      this.followUpSopEnabled = true;
      this.disableRegimenEdit.emit(true);
      this.updateFollowUpSopOptions(followUpSop);
      return;
    }
    let currentFollowUpSop = followUpSop;
    followUpSopTransition.forEach((transition: any) => {
      currentFollowUpSop = this.getFollowUpSopForCurrentTransition([...currentFollowUpSop], transition);
    });
    if (!currentFollowUpSop?.length) { return; }
    this.updateFollowUpSopOptions(currentFollowUpSop);
    this.followUpSop.currentFollowUpSop = currentFollowUpSop;
    this.followUpSopEnabled = true;
    this.disableRegimenEdit.emit(true);
  }

  updateFollowUpSopOptions(currentFollowUpSop: any): void {
    currentFollowUpSop.forEach((each: any) => {
      this.followUpSopOptions.push({ value: each.name, display: each.shortName || each.name });
    });
    this.followUpSopOptions.push({ value: 'other', display: 'other' });
  }

  getFollowUpSopForCurrentTransition(followUpSop_: any, transition: string): any {
    const [followUpSop]: any = followUpSop_.filter((each: any) => each.name === transition);
    return followUpSop.nextFollowUps;
  }

  async onSelectFollowUpSop(event: string): Promise<void> {
    if (event === 'other') {
      this.disableRegimenEdit.emit(false);
      this.changeRegimen.emit({ changes: [] });
      this.followUpSopOptions = [];
      this.followUpSopEnabled = false;
      const dialogRef = this.dialog.open(DataUploadModal,
        { data: {
          title: 'Reason for disabling FollowUpSOP',
          minCharLength: 10,
        } });
      dialogRef.afterClosed().subscribe(async (result: any) => {
        if (!result) {
          await this.fetchFollowUpSopOptions();
          return;
        }
        if (result) {
          const details = {
            reason: result,
            followUpCount: this.followUp.get('followUpCount'),
            doctor: this.connectionService.getCurrentUser(),
          };
          this.regimen.set('disableFollowUpSop', true);
          this.regimen.set('followUpSopDisableReason', details);
        }
      });
      return;
    }
    this.followUpSop.selectedFollowUpSop = this.followUpSop.currentFollowUpSop
      .find((each: any) => each.name === event);
    this.changeRegimen.emit({ changes: this.followUpSop.selectedFollowUpSop.changes,
      onHoldDuration: this.followUpSop.selectedFollowUpSop.onHoldDurationInDays});
    const audioTemplateName = `${this.regimen.get('regimenId').split('_personalized_')[0]}`
      + `_${this.followUpSop.selectedFollowUpSop.name}_${(this.followUp.get('followUpCount') - 1) || 0}`;
    this.changeVoiceNote.emit(this.followUpSop.selectedFollowUpSop.audioTemplateName || audioTemplateName);
    this.disableRegimenEdit.emit(true);
    this.reportObject.investigations = this.followUpSop.selectedFollowUpSop?.tests;
    this.reportObject.treatmentProgress = this.followUpSop.selectedFollowUpSop?.treatmentProgress;
    this.reportObject.treatmentOutcome = { selectedTreatmentOutcome: event };
    await this.updateFollowUpSopTransition(event);
    this.setCreateReport();
  }

  async updateFollowUpSopTransition(selectedFollowUpSopName: string): Promise<void> {
    const followUpSopTransition = (this.regimen.get('followUpSopTransition') || []).slice(0, this.followUp.get('followUpCount') - 1);
    this.regimen.set('followUpSopTransition', [...followUpSopTransition, selectedFollowUpSopName]);
    this.regimen.set('disableFollowUpSop', false);
  }

  getBackgroundColor(score: number): string {
    switch (score) {
      case 0: return '#F9E7D7';
      case 1: return '#F3F3F3';
      case 2: return '#F3F3F3';
      case 3: return '#EBF1CE';
      default: return '';
    }
  }

  getTextForScore(score: number): string {
    switch (score) {
      case 0: return 'INCREASED';
      case 1: return 'SAME';
      case 2: return 'BETTER';
      case 3: return 'GOOD';
      default: return '';
    }
  }

  async updateSteroidTag(updateTag: boolean): Promise<void> {
    if (updateTag && !this.user.get('marketingTags').includes(this.appConfig.Shared.User.MarketingTags.STEROID)) {
      this.user.set('marketingTags', [...this.user.get('marketingTags'), this.appConfig.Shared.User.MarketingTags.STEROID]);
      await this.user.save();
    }
    this.showSteroidQuestion = false;
  }

  async updatePregnancyStatus(status: string): Promise<void> {
    const currentTags = this.user.get('marketingTags') || [];
    const tagsWithoutPregnancy = currentTags.filter((tag: string) => !this.pregnancyTags.includes(tag));
    if (status === 'NONE') {
      this.user.set('marketingTags', tagsWithoutPregnancy);
    } else {
      this.user.set('marketingTags', [...tagsWithoutPregnancy, status]);
    }
    await this.user.save();
    this.broadcaster.broadcast('NOTIFY', {
      message: 'Pregnancy status updated successfully',
      type: this.appConfig.Shared.Toast.Type.SUCCESS,
    });
  }
}
